import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  apiErrorStatus,
  apiErrorText,
  apiLoadErrorText,
  useGetPayRequestQuery,
  useGetPayRequestsMetaQuery,
  useWithdrawPayRequestMutation,
  type PayRequestDetail,
  type PayRequestView,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Dialog } from '@shared/ui/Dialog';
import { IconArrowLeft } from '@shared/ui/icons';
import {
  finStatusTone,
  formatDate,
  formatDateTime,
  formatRub,
  invoiceLabel,
  stageHint,
  stageTone,
} from './model';
import { ClosingSection } from './ui/ClosingSection';
import { FilesList } from './ui/FilesList';
import {
  ErrorText,
  Flags,
  H1,
  HeaderRow,
  InfoGrid,
  Mono,
  Muted,
  Notice,
  Page,
  SectionCard,
  SectionHead,
  SectionTitle,
  Small,
  StagePill,
} from './ui/styled';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Money = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Tile = styled.div`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const TileLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TileValue = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  min-width: 0;
`;

const Parts = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Part = styled.li`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const PartHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  font-weight: 700;
`;

function vatText(r: PayRequestView): string {
  if (r.vat_amount == null) return 'НДС в счёте не указан';
  if (r.vat_amount === 0) return 'без НДС';
  return `в т.ч. НДС ${formatRub(r.vat_amount)}`;
}

/** Как заявка идёт в Фабрике платежей: основная и перенесённый остаток. */
function FinParts({ detail }: { detail: PayRequestDetail }) {
  const r = detail.request;
  const parts = detail.fin_parts;
  return (
    <SectionCard as="section" aria-labelledby="pay-request-parts">
      <SectionTitle id="pay-request-parts">Как движется заявка</SectionTitle>
      {parts.length === 0 ? (
        <Muted>
          {r.stage === 'withdrawn'
            ? 'Заявку отозвали — в реестре Финансиста её больше нет.'
            : 'В реестре Финансиста этой заявки нет — её сняли.'}
        </Muted>
      ) : (
        <Parts>
          {parts.map((p) => (
            <Part key={p.request_number}>
              <PartHead>
                {p.request_number}
                {p.is_carryover && <Badge $tone="neutral">перенос остатка</Badge>}
                <Badge $tone={finStatusTone(p.status)}>{p.status_label}</Badge>
              </PartHead>
              <Small>
                К оплате {formatRub(p.amount)}
                {p.approved_amount > 0 && ` · согласовано ${formatRub(p.approved_amount)}`}
                {p.paid_amount > 0 &&
                  ` · оплачено ${formatRub(p.paid_amount)}${
                    p.last_paid_date ? ` ${formatDate(p.last_paid_date)}` : ''
                  }`}
              </Small>
              {p.registry_number && <Small>Реестр {p.registry_number}</Small>}
            </Part>
          ))}
        </Parts>
      )}
      {parts.some((p) => p.is_carryover) && (
        <Small>
          Часть суммы согласовали сразу, остаток перенесли — он идёт отдельной строкой в следующем
          реестре.
        </Small>
      )}
    </SectionCard>
  );
}

/** Карточка заявки на оплату (`/pay-requests/:id`). */
export function PayRequestCard() {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const createdState = (location.state as { created?: boolean } | null)?.created === true;
  const [justCreated, setJustCreated] = useState(createdState);
  const metaQ = useGetPayRequestsMetaQuery();
  const q = useGetPayRequestQuery(id, { skip: !id, refetchOnMountOrArgChange: 30 });
  const [withdraw, { isLoading: withdrawing }] = useWithdrawPayRequestMutation();
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const keepRef = useRef<HTMLButtonElement>(null);

  // «Передана Финансисту» — один раз: после обновления страницы плашки уже нет.
  useEffect(() => {
    if (createdState) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
    // Только при открытии карточки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function confirmWithdraw() {
    setWithdrawError(null);
    try {
      await withdraw(id).unwrap();
      setWithdrawOpen(false);
    } catch (err) {
      setWithdrawError(apiErrorText(err, 'Заявка не отозвана.'));
    }
  }

  const back = (
    <BackLink to="/pay-requests">
      <IconArrowLeft size={14} />
      Заявки на оплату
    </BackLink>
  );

  if (q.isLoading) {
    return (
      <Page>
        {back}
        <Muted>Загружаем заявку…</Muted>
      </Page>
    );
  }
  if (!q.data) {
    return (
      <Page>
        {back}
        <ErrorText role="alert">
          {apiErrorStatus(q.error) === 404
            ? 'Заявка не найдена или у вас нет к ней доступа.'
            : apiLoadErrorText(
                q.error,
                'Заявка не загрузилась.',
                'У вас нет доступа к этой заявке.',
              )}
        </ErrorText>
      </Page>
    );
  }

  const detail = q.data;
  const r = detail.request;
  const invoiceFiles = detail.files.filter((f) => f.kind === 'invoice');
  const invoice = invoiceLabel(r);
  const hasBank = Boolean(
    r.recipient_account_number ||
    r.recipient_bik ||
    r.recipient_bank_name ||
    r.recipient_corr_account,
  );

  return (
    <Page>
      {back}

      {justCreated && (
        <Notice $tone="success" role="status">
          <strong>
            Заявка {r.request_number ?? ''} передана Финансисту
            {r.registry_number ? `, она в реестре ${r.registry_number}` : ''}.
          </strong>
          Когда Финансист или Гендиректор примут решение, придёт уведомление в колокольчике.{' '}
          <Button type="button" $variant="ghost" $size="sm" onClick={() => setJustCreated(false)}>
            Понятно
          </Button>
        </Notice>
      )}

      <HeaderRow>
        <div>
          <H1>Заявка на оплату {r.request_number ?? ''}</H1>
          <Muted>
            {r.initiator_name}
            {r.department_name ? `, ${r.department_name}` : ''} · подана{' '}
            {formatDateTime(r.created_at)}
            {r.withdrawn_at && ` · отозвана ${formatDateTime(r.withdrawn_at)}`}
          </Muted>
        </div>
        {r.can_withdraw && (
          <Button
            type="button"
            $variant="secondary"
            onClick={() => {
              setWithdrawError(null);
              setWithdrawOpen(true);
            }}
          >
            Отозвать заявку
          </Button>
        )}
      </HeaderRow>

      <SectionCard>
        <Flags>
          <StagePill $tone={stageTone(r.stage)}>{r.stage_label}</StagePill>
          {r.urgent && <Badge $tone="danger">Срочно</Badge>}
        </Flags>
        <Muted>{stageHint(r)}</Muted>
        {/* Причина последней отклонённой или отложенной части — при любом этапе: остаток
            могли отклонить и у согласованной заявки. Текст (и шаблон Гендиректора) — как есть. */}
        {r.decision_reason && (
          <Notice $tone={r.stage === 'rejected' ? 'danger' : 'warning'}>
            <strong>
              {r.stage === 'rejected'
                ? 'Причина отказа'
                : r.stage === 'deferred'
                  ? 'Причина отсрочки'
                  : 'Часть заявки отклонена или отложена'}
            </strong>
            {r.decision_reason}
          </Notice>
        )}
        <Money>
          <Tile>
            <TileLabel>К оплате</TileLabel>
            <TileValue>{formatRub(r.amount)}</TileValue>
            <Small>{vatText(r)}</Small>
          </Tile>
          <Tile>
            <TileLabel>Согласовано</TileLabel>
            <TileValue>{r.approved_total > 0 ? formatRub(r.approved_total) : '—'}</TileValue>
            {r.partially_approved && <Small>не вся сумма</Small>}
          </Tile>
          <Tile>
            <TileLabel>Оплачено</TileLabel>
            <TileValue>{r.paid_total > 0 ? formatRub(r.paid_total) : '—'}</TileValue>
            {r.first_paid_date && <Small>первая оплата {formatDate(r.first_paid_date)}</Small>}
          </Tile>
        </Money>
      </SectionCard>

      <Columns>
        <Column>
          <SectionCard as="section" aria-labelledby="pay-request-supplier">
            <SectionTitle id="pay-request-supplier">Поставщик и счёт</SectionTitle>
            <InfoGrid>
              <dt>Поставщик</dt>
              <dd>{r.counterparty_name}</dd>
              {(r.counterparty_inn || r.counterparty_kpp) && (
                <>
                  <dt>ИНН / КПП</dt>
                  <dd>
                    <Mono>
                      {r.counterparty_inn ?? '—'}
                      {r.counterparty_kpp ? ` / ${r.counterparty_kpp}` : ''}
                    </Mono>
                  </dd>
                </>
              )}
              <dt>Счёт</dt>
              <dd>{invoice || '—'}</dd>
              <dt>Назначение платежа</dt>
              <dd>{r.purpose}</dd>
              <dt>Статья</dt>
              <dd>{r.category_label}</dd>
              {r.desired_pay_date && (
                <>
                  <dt>Желаемая дата оплаты</dt>
                  <dd>{formatDate(r.desired_pay_date)}</dd>
                </>
              )}
              {r.project_name && (
                <>
                  <dt>Объект</dt>
                  <dd>{r.project_name}</dd>
                </>
              )}
              {r.contract_number && (
                <>
                  <dt>Договор</dt>
                  <dd>{r.contract_number}</dd>
                </>
              )}
              <dt>Отдел</dt>
              <dd>{r.department_name || 'Без отдела'}</dd>
              {r.comment && (
                <>
                  <dt>Комментарий для Финансиста</dt>
                  <dd>{r.comment}</dd>
                </>
              )}
              {hasBank && (
                <>
                  <dt>Банк</dt>
                  <dd>
                    {r.recipient_bank_name || '—'}
                    {r.recipient_bik && (
                      <Small>
                        БИК <Mono>{r.recipient_bik}</Mono>
                      </Small>
                    )}
                  </dd>
                  <dt>Расчётный счёт</dt>
                  <dd>
                    <Mono>{r.recipient_account_number ?? '—'}</Mono>
                  </dd>
                  {r.recipient_corr_account && (
                    <>
                      <dt>Корр. счёт</dt>
                      <dd>
                        <Mono>{r.recipient_corr_account}</Mono>
                      </dd>
                    </>
                  )}
                </>
              )}
            </InfoGrid>
          </SectionCard>

          <SectionCard as="section" aria-labelledby="pay-request-invoice">
            <SectionHead>
              <SectionTitle id="pay-request-invoice">Файл счёта</SectionTitle>
            </SectionHead>
            {invoiceFiles.length > 0 ? (
              <FilesList requestId={r.id} files={invoiceFiles} />
            ) : (
              <Small>Файла счёта нет.</Small>
            )}
          </SectionCard>
        </Column>

        <Column>
          <FinParts detail={detail} />
          <ClosingSection detail={detail} maxFileMb={metaQ.data?.max_file_mb ?? 20} />
        </Column>
      </Columns>

      <Dialog
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        title="Отозвать заявку?"
        role="alertdialog"
        initialFocusRef={keepRef}
        footer={
          <>
            <Button
              ref={keepRef}
              type="button"
              $variant="secondary"
              onClick={() => setWithdrawOpen(false)}
            >
              Не отзывать
            </Button>
            <Button
              type="button"
              $variant="danger"
              onClick={() => void confirmWithdraw()}
              disabled={withdrawing}
            >
              {withdrawing ? 'Отзываем…' : 'Отозвать'}
            </Button>
          </>
        }
      >
        <p>
          Заявка {r.request_number ?? ''} уйдёт из реестра Финансиста — счёт не оплатят. Вернуть её
          не получится: если оплата всё же понадобится, подайте новую заявку.
        </p>
        {withdrawError && <ErrorText role="alert">{withdrawError}</ErrorText>}
      </Dialog>
    </Page>
  );
}
